// AGC: Whitepaper component - Accordion style for mobile-friendly viewing
// AGC: Displays the complete blockchain technical whitepaper with expandable sections
import React, { useState } from 'react'
import { ChevronDown, ChevronUp, ArrowLeft, BookOpen } from 'lucide-react'
import { playSoundSafe } from '../utils/audio'

interface WhitepaperProps {
  onBack: () => void
}

// AGC: Accordion item component for expandable sections
interface AccordionItemProps {
  id: string
  title: string
  icon?: string
  children: React.ReactNode
  defaultOpen?: boolean
  isOpen: boolean
  onToggle: () => void
}

const AccordionItem: React.FC<AccordionItemProps> = ({ id, title, icon, children, isOpen, onToggle }) => {
  const toggle = () => {
    playSoundSafe('/sounds/click.mp3', 0.2)
    onToggle()
  }

  return (
    <div className="border-b border-slate-700/50">
      <button
        onClick={toggle}
        className="w-full flex items-center justify-between p-4 text-left hover:bg-slate-800/50 transition-colors"
      >
        <div className="flex items-center gap-3">
          {icon && <span className="text-xl">{icon}</span>}
          <h3 className="text-base font-semibold text-white">{title}</h3>
        </div>
        {isOpen ? (
          <ChevronUp className="w-5 h-5 text-slate-400 flex-shrink-0" />
        ) : (
          <ChevronDown className="w-5 h-5 text-slate-400 flex-shrink-0" />
        )}
      </button>
      {isOpen && (
        <div className="px-4 pb-4 text-sm text-slate-300 space-y-4 animate-in fade-in slide-in-from-top-2 duration-200">
          {children}
        </div>
      )}
    </div>
  )
}

// AGC: Contract address link component
const ContractLink: React.FC<{ address: string; network?: 'sepolia' | 'mainnet' }> = ({ address, network = 'sepolia' }) => {
  const baseUrl = network === 'sepolia' 
    ? 'https://sepolia.basescan.org/address'
    : 'https://basescan.org/address'
  
  return (
    <a
      href={`${baseUrl}/${address}`}
      target="_blank"
      rel="noopener noreferrer"
      className="text-blue-400 hover:text-blue-300 underline font-mono break-all"
      onClick={(e) => e.stopPropagation()}
    >
      {address}
    </a>
  )
}

// AGC: Table of Contents component
const TableOfContents: React.FC<{ sections: Array<{ id: string; title: string; icon?: string }>; onNavigate: (id: string) => void }> = ({ sections, onNavigate }) => {
  const [isExpanded, setIsExpanded] = useState(false)

  return (
    <div className="bg-slate-800/50 rounded-lg p-4 mb-4 border border-slate-700">
      <button
        onClick={() => {
          playSoundSafe('/sounds/click.mp3', 0.2)
          setIsExpanded(!isExpanded)
        }}
        className="w-full flex items-center justify-between"
      >
        <div className="flex items-center gap-2">
          <BookOpen className="w-5 h-5 text-blue-400" />
          <h2 className="text-lg font-semibold text-white">Table of Contents</h2>
        </div>
        {isExpanded ? (
          <ChevronUp className="w-5 h-5 text-slate-400" />
        ) : (
          <ChevronDown className="w-5 h-5 text-slate-400" />
        )}
      </button>
      {isExpanded && (
        <div className="mt-4 space-y-2">
          {sections.map((section) => (
            <button
              key={section.id}
              onClick={() => {
                playSoundSafe('/sounds/click.mp3', 0.2)
                onNavigate(section.id)
                setIsExpanded(false)
              }}
              className="w-full text-left px-3 py-2 rounded-lg hover:bg-slate-700/50 transition-colors text-sm text-slate-300 flex items-center gap-2"
            >
              {section.icon && <span>{section.icon}</span>}
              <span>{section.title}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

export const Whitepaper: React.FC<WhitepaperProps> = ({ onBack }) => {
  const [openSections, setOpenSections] = useState<Set<string>>(new Set(['executive-summary']))

  // AGC: Navigate to section by ID
  const navigateToSection = (id: string) => {
    setOpenSections((prev) => new Set([...prev, id]))
    setTimeout(() => {
      const element = document.getElementById(id)
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    }, 100)
  }

  // AGC: Toggle section
  const toggleSection = (id: string) => {
    setOpenSections((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  // AGC: Check if section is open
  const isSectionOpen = (id: string) => openSections.has(id)

  // AGC: All sections data
  const sections = [
    { id: 'executive-summary', title: 'Executive Summary', icon: '📋' },
    { id: 'blockchain-architecture', title: 'Blockchain Architecture Overview', icon: '🏗️' },
    { id: 'token-economics', title: 'Token Economics: FarmPro Token', icon: '🪙' },
    { id: 'quest-flywheel', title: 'Quest & Rewards Flywheel', icon: '🎯' },
    { id: 'refund-system', title: 'Comprehensive Refund System', icon: '💰' },
    { id: 'chainlink-vrf', title: 'Chainlink VRF: Provably Fair Randomness', icon: '🎲' },
    { id: 'battle-system', title: 'Battle System: On-Chain Game Logic', icon: '🎮' },
    { id: 'nft-system', title: 'NFT System: ERC1155 Multi-Token Standard', icon: '🛡️' },
    { id: 'staking-revenue', title: 'Staking & Revenue Sharing System', icon: '💰' },
    { id: 'tournament-system', title: 'Tournament System', icon: '🏆' },
    { id: 'game-treasury', title: 'Game Treasury', icon: '🏛️' },
    { id: 'security-architecture', title: 'Security Architecture', icon: '🔒' },
    { id: 'economic-models', title: 'Economic Models & Formulas', icon: '📊' },
    { id: 'contract-addresses', title: 'Contract Addresses', icon: '🔗' },
    { id: 'recent-updates', title: 'Recent Updates & Enhancements', icon: '📅' },
    { id: 'roadmap', title: 'Roadmap & Future Development', icon: '🚀' },
    { id: 'investment-thesis', title: 'Investment Thesis', icon: '📈' },
    { id: 'contact-resources', title: 'Contact & Resources', icon: '📞' },
    { id: 'conclusion', title: 'Conclusion', icon: '📝' },
  ]

  return (
    <main className="pt-0 pb-[88px] px-3 min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950">
      <div className="max-w-4xl mx-auto">
        {/* AGC: Header with back button */}
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={() => {
              playSoundSafe('/sounds/click.mp3', 0.3)
              onBack()
            }}
            className="p-2 rounded-lg bg-slate-800 hover:bg-slate-700 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-white" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-white">Blockchain Technical Whitepaper</h1>
            <p className="text-sm text-slate-400">Version 1.1.0 · Last Updated: December 1, 2025</p>
          </div>
        </div>

        {/* AGC: Document metadata */}
        <div className="bg-slate-800/50 rounded-lg p-4 mb-6 border border-slate-700">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <p className="text-slate-400 text-xs">Network</p>
              <p className="text-white font-semibold">Base Sepolia</p>
            </div>
            <div>
              <p className="text-slate-400 text-xs">Token Standard</p>
              <p className="text-white font-semibold">ERC20 (Zora)</p>
            </div>
            <div>
              <p className="text-slate-400 text-xs">Blockchain</p>
              <p className="text-white font-semibold">Base (L2)</p>
            </div>
            <div>
              <p className="text-slate-400 text-xs">Status</p>
              <p className="text-white font-semibold">Testnet</p>
            </div>
          </div>
        </div>

        {/* AGC: Table of Contents */}
        <TableOfContents sections={sections} onNavigate={navigateToSection} />

        {/* AGC: Executive Summary - Always visible first */}
        <div id="executive-summary" className="bg-slate-900/80 rounded-lg p-6 mb-4 border border-slate-800">
          <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
            <span>📋</span>
            Executive Summary
          </h2>
          <div className="space-y-4 text-sm text-slate-300">
            <p>
              Origami Battles represents a paradigm shift in blockchain gaming, combining strategic turn-based combat with fully on-chain game mechanics, verifiable randomness, and a comprehensive token economy. Built on Base (Coinbase's Ethereum L2), the game leverages Chainlink VRF for provably fair randomness, ERC1155 NFTs for in-game assets, and a sophisticated staking and revenue-sharing system that creates sustainable value accrual for token holders.
            </p>
            <div className="bg-blue-900/20 border border-blue-700/50 rounded-lg p-4">
              <p className="font-semibold text-blue-300 mb-2">Quest & Rewards Flywheel</p>
              <p className="text-sm">
                The game implements a self-sustaining economic flywheel where players use tokens to battle (burning 15% per battle), complete quests to earn rewards (10-500 FarmPro tokens), use NFTs that get consumed in battles (creating scarcity), and stake tokens to earn revenue share. This creates a continuous cycle: play → burn → earn → stake → play.
              </p>
            </div>
            <div>
              <p className="font-semibold text-white mb-2">Key Investment Highlights:</p>
              <ul className="space-y-2 list-disc list-inside text-slate-300">
                <li><strong className="text-white">100% On-Chain Game Logic</strong>: Battle outcomes, rarity rolls, buff activations, player XP, battle history, staking positions, revenue share, tournament data, NFT ownership, and all game state are verifiable on-chain</li>
                <li><strong className="text-white">Chainlink VRF Integration</strong>: Industry-leading oracle for tamper-proof randomness</li>
                <li><strong className="text-white">Fair Launch via Zora Creator Coin</strong>: FarmPro token leverages Zora's Creator Coin infrastructure for transparent, community-driven token distribution</li>
                <li><strong className="text-white">Deflationary Token Economics</strong>: Multi-vector burn mechanism (15% per battle, NFT consumption, marketplace fees) creates sustainable deflationary pressure</li>
                <li><strong className="text-white">Revenue Sharing Model</strong>: Stakers earn from marketplace revenue (ETH, USDC, FarmPro) with weekly on-chain distribution</li>
                <li><strong className="text-white">Security-First Architecture</strong>: Multi-sig governance, emergency withdrawal mechanisms, and comprehensive access controls</li>
              </ul>
            </div>
          </div>
        </div>

        {/* AGC: All other sections as accordions */}
        <div className="bg-slate-900/80 rounded-lg border border-slate-800 overflow-hidden">
          {/* Blockchain Architecture */}
          <div id="blockchain-architecture">
            <AccordionItem id="blockchain-architecture" title="Blockchain Architecture Overview" icon="🏗️" isOpen={isSectionOpen('blockchain-architecture')} onToggle={() => toggleSection('blockchain-architecture')}>
            <div className="space-y-4">
              <div>
                <h4 className="font-semibold text-white mb-2">Network Infrastructure</h4>
                <div className="bg-slate-800/50 rounded p-3">
                  <p className="font-semibold text-blue-300 mb-2">Base Network (Ethereum L2)</p>
                  <ul className="space-y-1 text-xs">
                    <li><strong>Chain ID:</strong> 8453 (Mainnet), 84532 (Sepolia Testnet)</li>
                    <li><strong>RPC Endpoint:</strong> https://mainnet.base.org / https://sepolia.base.org</li>
                    <li><strong>Block Explorer:</strong> BaseScan (https://basescan.org)</li>
                    <li><strong>Gas Token:</strong> ETH (native)</li>
                    <li><strong>Transaction Finality:</strong> ~2 seconds (L2 confirmation)</li>
                    <li><strong>Cost Efficiency:</strong> ~10-100x cheaper than Ethereum mainnet</li>
                  </ul>
                </div>
                <div className="mt-3">
                  <p className="font-semibold text-white mb-2">Why Base?</p>
                  <ul className="space-y-1 text-xs list-disc list-inside">
                    <li>Coinbase-backed infrastructure with institutional credibility</li>
                    <li>Native integration with Coinbase Wallet and Base App</li>
                    <li>Low transaction costs enable micro-transactions ($0.01 USDC battles)</li>
                    <li>High throughput (100+ TPS) supports real-time gaming</li>
                    <li>EVM-compatible, enabling seamless integration with existing tooling</li>
                  </ul>
                </div>
              </div>
              <div>
                <h4 className="font-semibold text-white mb-2">Smart Contract Architecture</h4>
                <p className="text-xs mb-2">
                  The game consists of <strong>15+ production smart contracts</strong> deployed on Base, each serving a specific purpose in the ecosystem:
                </p>
                <div className="bg-slate-800/50 rounded p-3 font-mono text-xs">
                  <div className="space-y-2">
                    <div>┌─ FarmPro Token (ERC20)</div>
                    <div>├─ Buff1155 (ERC1155)</div>
                    <div>├─ Potion1155 (ERC1155)</div>
                    <div>├─ P2P Battle Manager V2</div>
                    <div>├─ PvE Manager V7</div>
                    <div>├─ RNG Beacon (Chainlink VRF)</div>
                    <div>├─ Game Treasury</div>
                    <div>├─ Tournament Contract</div>
                    <div>├─ Staking Manager V4</div>
                    <div>├─ Revenue Share V4</div>
                    <div>└─ Staking Mirror</div>
                  </div>
                </div>
              </div>
            </div>
          </AccordionItem>
          </div>

          {/* Token Economics */}
          <div id="token-economics">
            <AccordionItem id="token-economics" title="Token Economics: FarmPro Token (Zora Creator Coin)" icon="🪙" isOpen={isSectionOpen('token-economics')} onToggle={() => toggleSection('token-economics')}>
            <div className="space-y-4">
              <div>
                <h4 className="font-semibold text-white mb-2">Token Overview</h4>
                <p className="text-xs mb-3">
                  <strong>FarmPro Token</strong> is the native ERC20 token powering the Origami Battles ecosystem. Built on Zora's Creator Coin standard, it combines the security of ERC20 with creator economy features.
                </p>
                <div className="bg-slate-800/50 rounded p-3 text-xs space-y-1">
                  <p><strong>Contract Address (Base Sepolia):</strong> <ContractLink address="0x0a862e9bE2716162f14BE1cb500b29354ED3D434" network="sepolia" /></p>
                  <p><strong>Zora Creator Coin (Mainnet):</strong> <ContractLink address="0x9c66c908f5dba3fa007e198cc1cb5eb37302c28e" network="mainnet" /></p>
                  <p><strong>Standard:</strong> ERC20 (Zora Creator Coin)</p>
                  <p><strong>Decimals:</strong> 18</p>
                  <p><strong>Distribution Model:</strong> Fair launch via Zora Creator Coin bonding curve</p>
                </div>
              </div>
              <div>
                <h4 className="font-semibold text-white mb-2">Token Utility</h4>
                <div className="space-y-3 text-xs">
                  <div>
                    <p className="font-semibold text-blue-300 mb-1">1. Battle Entry Fees</p>
                    <ul className="list-disc list-inside space-y-1 text-slate-300">
                      <li>P2P Battles: 50 FarmPro per player (100 FarmPro total pool)</li>
                      <li>PvE Battles: 50 FarmPro per battle</li>
                      <li>Tournament Entry: 50 FarmPro per player</li>
                    </ul>
                  </div>
                  <div>
                    <p className="font-semibold text-blue-300 mb-1">2. Staking Rewards</p>
                    <ul className="list-disc list-inside space-y-1 text-slate-300">
                      <li>Quest Rewards: Daily quest completion bonuses</li>
                      <li>Revenue Share: Weekly distribution from marketplace revenue</li>
                      <li>Tournament Prizes: Top 3 finishers receive FarmPro rewards</li>
                      <li>Marketplace Discount: 50% off all NFT purchases when staking 500+ FarmPro</li>
                    </ul>
                  </div>
                  <div>
                    <p className="font-semibold text-blue-300 mb-1">3. Marketplace Currency</p>
                    <p className="text-slate-300">Purchase Buff NFTs and Potion NFTs with FarmPro</p>
                  </div>
                  <div>
                    <p className="font-semibold text-blue-300 mb-1">4. New User Airdrop System</p>
                    <ul className="list-disc list-inside space-y-1 text-slate-300">
                      <li>Token Airdrop: New users receive variable amounts (1,000-2,000 FarmPro tokens)</li>
                      <li>NFT Welcome Bundle: 10 units of each buff type (40 total NFTs)</li>
                      <li>Security: EIP-712 signed vouchers prevent bot abuse</li>
                    </ul>
                  </div>
                </div>
              </div>
              <div>
                <h4 className="font-semibold text-white mb-2">Battle Economics</h4>
                <div className="bg-slate-800/50 rounded p-3 font-mono text-xs space-y-1">
                  <p>P2P Battle Pool = 100 FarmPro (50 per player)</p>
                  <p>├─ Winner Reward: 85 FarmPro (85% of pool)</p>
                  <p>├─ Burn Amount: 15 FarmPro (15% deflationary)</p>
                  <p>└─ Net Winner Profit: +35 FarmPro (+70% ROI)</p>
                  <p className="mt-2">PvE Battle Pool = 50 FarmPro (player only)</p>
                  <p>├─ Winner Reward: 85 FarmPro (if player wins)</p>
                  <p>├─ Burn Amount: 15 FarmPro (on win/loss)</p>
                  <p>└─ Draw Refund: 40 FarmPro each (80 FarmPro total)</p>
                </div>
              </div>
              <div>
                <h4 className="font-semibold text-white mb-2">Deflationary Mechanisms</h4>
                <div className="space-y-2 text-xs">
                  <div>
                    <p className="font-semibold text-green-300">1. Battle Burns (Primary)</p>
                    <p className="text-slate-300">P2P Win/Loss: 15 FarmPro burned per battle (15% of pool)</p>
                    <p className="text-slate-300">PvE Win/Loss: 15 FarmPro burned per battle (15% of stake)</p>
                  </div>
                  <div>
                    <p className="font-semibold text-green-300">2. NFT Consumption Burns</p>
                    <p className="text-slate-300">Buff NFTs and Potion NFTs burned after use in battle</p>
                    <p className="text-slate-300">Daily Estimate (1000 battles): ~3,000 NFTs/day permanently removed</p>
                  </div>
                  <div>
                    <p className="font-semibold text-green-300">3. Marketplace Fee Burns</p>
                    <p className="text-slate-300">2-5% of NFT marketplace transaction value burned</p>
                  </div>
                  <div className="bg-green-900/20 border border-green-700/50 rounded p-3 mt-3">
                    <p className="font-semibold text-green-300 mb-1">Total Daily Burn Estimate</p>
                    <p className="text-xs text-slate-300">~16,000-20,000 FarmPro/day at scale</p>
                    <p className="text-xs text-slate-300">Annual: ~5.8M-7.3M FarmPro/year</p>
                  </div>
                </div>
              </div>
            </div>
          </AccordionItem>
          </div>

          {/* Quest & Rewards Flywheel */}
          <div id="quest-flywheel">
            <AccordionItem id="quest-flywheel" title="Quest & Rewards Flywheel: Self-Sustaining Economic Loop" icon="🎯" isOpen={isSectionOpen('quest-flywheel')} onToggle={() => toggleSection('quest-flywheel')}>
            <div className="space-y-4">
              <div>
                <h4 className="font-semibold text-white mb-2">Flywheel Overview</h4>
                <p className="text-xs mb-3">
                  <strong>Core Concept:</strong> Players use tokens to play → tokens get burned → players earn rewards through quests → rewards enable more gameplay → cycle continues with deflationary pressure.
                </p>
                <div className="bg-slate-800/50 rounded p-3 font-mono text-xs space-y-1">
                  <p>1. PLAY BATTLES (Spend 50 FarmPro per battle)</p>
                  <p>2. TOKEN BURNS (15 FarmPro burned per battle)</p>
                  <p>3. EARN REWARDS (Complete quests: 10-500 FarmPro)</p>
                  <p>4. REINVEST (Buy NFTs, stake tokens, play more)</p>
                  <p>5. REPEAT CYCLE</p>
                </div>
              </div>
              <div>
                <h4 className="font-semibold text-white mb-2">Quest System Architecture</h4>
                <div className="space-y-3 text-xs">
                  <div>
                    <p className="font-semibold text-blue-300 mb-1">Quest Categories:</p>
                    <ul className="list-disc list-inside space-y-1 text-slate-300">
                      <li><strong>Daily Quests:</strong> 10 quests, reset every 24 hours</li>
                      <li><strong>Weekly Quests:</strong> 10 quests, reset every Monday</li>
                      <li><strong>Monthly Quests:</strong> 10 quests, reset on 1st of month</li>
                    </ul>
                  </div>
                  <div>
                    <p className="font-semibold text-blue-300 mb-1">Quest Difficulty Tiers:</p>
                    <ul className="list-disc list-inside space-y-1 text-slate-300">
                      <li><strong>Easy:</strong> 10-30 FarmPro rewards, 1 quest point</li>
                      <li><strong>Medium:</strong> 20-60 FarmPro rewards, 2 quest points</li>
                      <li><strong>Hard:</strong> 30-500 FarmPro rewards, 3 quest points</li>
                    </ul>
                  </div>
                  <div className="bg-blue-900/20 border border-blue-700/50 rounded p-3">
                    <p className="font-semibold text-blue-300 mb-2">Maximum Potential Earnings:</p>
                    <ul className="space-y-1 text-xs text-slate-300">
                      <li><strong>Daily:</strong> 320 FarmPro/day (all quests + set bonus)</li>
                      <li><strong>Weekly:</strong> 760 FarmPro/week (all quests + set bonus)</li>
                      <li><strong>Monthly:</strong> 2,700 FarmPro/month (all quests + set bonus)</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </AccordionItem>
          </div>

          {/* Refund System */}
          <div id="refund-system">
            <AccordionItem id="refund-system" title="Comprehensive Refund System" icon="💰" isOpen={isSectionOpen('refund-system')} onToggle={() => toggleSection('refund-system')}>
            <div className="space-y-4 text-xs">
              <p className="text-slate-300">
                Origami Battles implements a comprehensive, on-chain refund system that protects players in all scenarios. All refunds are executed automatically by smart contracts.
              </p>
              <div>
                <h4 className="font-semibold text-white mb-2">P2P Battle Refunds</h4>
                <div className="space-y-2">
                  <div className="bg-slate-800/50 rounded p-3">
                    <p className="font-semibold text-blue-300 mb-1">Payment Timeout (Opponent Doesn't Pay)</p>
                    <p className="text-slate-300">Condition: Battle expires (3 minutes) and opponent hasn't paid</p>
                    <p className="text-slate-300">Refund: Full refund (50 FarmPro + all escrowed NFTs)</p>
                  </div>
                  <div className="bg-slate-800/50 rounded p-3">
                    <p className="font-semibold text-blue-300 mb-1">Emergency Cancel</p>
                    <p className="text-slate-300">Condition: Both players paid, battle expired, but not resolved</p>
                    <p className="text-slate-300">Refund: Full refund to both players (50 FarmPro each + all NFTs)</p>
                  </div>
                  <div className="bg-slate-800/50 rounded p-3">
                    <p className="font-semibold text-blue-300 mb-1">Draw Refund</p>
                    <p className="text-slate-300">Refund: 40 FarmPro to each player (80% refund)</p>
                    <p className="text-slate-300">Burn: 20 FarmPro total burned (20% of pool)</p>
                  </div>
                  <div className="bg-slate-800/50 rounded p-3">
                    <p className="font-semibold text-blue-300 mb-1">Loser NFT Refund</p>
                    <p className="text-slate-300">Winner: 85 FarmPro reward, NFTs burned</p>
                    <p className="text-slate-300">Loser: 0 FarmPro (stake lost), NFTs REFUNDED</p>
                  </div>
                </div>
              </div>
              <div>
                <h4 className="font-semibold text-white mb-2">PvE Battle Refunds</h4>
                <div className="bg-slate-800/50 rounded p-3">
                  <p className="font-semibold text-blue-300 mb-1">Expired Battle (24-Hour Timeout)</p>
                  <p className="text-slate-300">Refund: Full refund (50 FarmPro + all escrowed NFTs)</p>
                </div>
              </div>
              <div>
                <h4 className="font-semibold text-white mb-2">Tournament Refunds</h4>
                <div className="space-y-2">
                  <div className="bg-slate-800/50 rounded p-3">
                    <p className="font-semibold text-blue-300 mb-1">Tournament Cancellation</p>
                    <p className="text-slate-300">Refund: Full entry fee refund (50 FarmPro) to all players</p>
                  </div>
                  <div className="bg-slate-800/50 rounded p-3">
                    <p className="font-semibold text-blue-300 mb-1">Tournament Timeout (70 Minutes)</p>
                    <p className="text-slate-300">Refund: Full entry fee refund (50 FarmPro) to all players</p>
                  </div>
                </div>
              </div>
            </div>
          </AccordionItem>
          </div>

          {/* Chainlink VRF */}
          <div id="chainlink-vrf">
            <AccordionItem id="chainlink-vrf" title="Chainlink VRF: Provably Fair Randomness" icon="🎲" isOpen={isSectionOpen('chainlink-vrf')} onToggle={() => toggleSection('chainlink-vrf')}>
            <div className="space-y-4 text-xs">
              <div>
                <h4 className="font-semibold text-white mb-2">Why Chainlink VRF?</h4>
                <ul className="list-disc list-inside space-y-1 text-slate-300">
                  <li><strong>Tamper-Proof Randomness:</strong> Random numbers generated off-chain by Chainlink oracles and verified on-chain</li>
                  <li><strong>Public Verifiability:</strong> Anyone can verify that randomness was generated correctly</li>
                  <li><strong>Unpredictability:</strong> Even Chainlink cannot predict or manipulate the random numbers</li>
                  <li><strong>Fairness:</strong> Players cannot exploit randomness for advantage</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-white mb-2">Technical Implementation</h4>
                <div className="bg-slate-800/50 rounded p-3 space-y-1">
                  <p><strong>Contract Address (Base Sepolia):</strong> <ContractLink address="0xFCBbe67fA163b8526Cb6882b2b4144aF33dE3082" network="sepolia" /></p>
                  <p><strong>VRF Coordinator:</strong> <ContractLink address="0x9DdfaCa8183c41ad55329BdeeD9F6A8d53168B1B" network="sepolia" /></p>
                  <p><strong>Callback Gas Limit:</strong> 100,000 gas</p>
                  <p><strong>Request Confirmations:</strong> 3 block confirmations</p>
                </div>
              </div>
              <div>
                <h4 className="font-semibold text-white mb-2">Randomness Request Flow</h4>
                <div className="bg-slate-800/50 rounded p-3 font-mono text-xs space-y-1">
                  <p>1. Battle Starts</p>
                  <p>2. Backend calls requestBattleRandomness(matchId, turn)</p>
                  <p>3. OrigamiRngBeacon requests randomness from Chainlink VRF</p>
                  <p>4. Chainlink oracle generates random seed (off-chain)</p>
                  <p>5. Random seed delivered to fulfillRandomWords() callback</p>
                  <p>6. Seed stored on-chain: matches[requestId].randomSeed</p>
                  <p>7. Game engine derives multiple random values from seed</p>
                </div>
              </div>
              <div>
                <h4 className="font-semibold text-white mb-2">Rarity Distribution</h4>
                <div className="bg-slate-800/50 rounded p-3 space-y-1">
                  <p><strong>DIAMOND:</strong> RNG &lt; 1,000 (0.1% chance) → +50 damage</p>
                  <p><strong>GOLD:</strong> RNG &lt; 11,000 (1.0% chance) → +25 damage</p>
                  <p><strong>SILVER:</strong> RNG &lt; 61,000 (5.0% chance) → +10 damage</p>
                  <p><strong>BRONZE:</strong> RNG &lt; 161,000 (15.0% chance) → +5 damage</p>
                  <p><strong>NONE:</strong> RNG ≥ 161,000 (78.9% chance) → +0 damage</p>
                </div>
              </div>
              <div>
                <h4 className="font-semibold text-white mb-2">Buff Activation System</h4>
                <p className="text-slate-300 mb-2">
                  <strong>Implementation Status:</strong> ✅ Fully Implemented - Buff activation is completely on-chain and verifiable using Chainlink VRF.
                </p>
                <div className="bg-slate-800/50 rounded p-3 space-y-2">
                  <p className="font-semibold text-blue-300">Buff Types:</p>
                  <ul className="list-disc list-inside space-y-1 text-slate-300">
                    <li><strong>Shield (ID: 0):</strong> 25% damage reduction</li>
                    <li><strong>Pierce (ID: 1):</strong> Reduces enemy defense</li>
                    <li><strong>Haste (ID: 2):</strong> +5 flat damage</li>
                    <li><strong>Frost (ID: 3):</strong> Freeze opponent (skip turn)</li>
                  </ul>
                  <p className="font-semibold text-blue-300 mt-3">Buff Proc Rates (Single Buff):</p>
                  <ul className="list-disc list-inside space-y-1 text-slate-300">
                    <li>FROST: 30% chance</li>
                    <li>SHIELD: 40% chance</li>
                    <li>PIERCE: 50% chance</li>
                    <li>HASTE: 60% chance</li>
                  </ul>
                </div>
              </div>
            </div>
          </AccordionItem>
          </div>

          {/* Battle System */}
          <div id="battle-system">
            <AccordionItem id="battle-system" title="Battle System: On-Chain Game Logic" icon="🎮" isOpen={isSectionOpen('battle-system')} onToggle={() => toggleSection('battle-system')}>
            <div className="space-y-4 text-xs">
              <div>
                <h4 className="font-semibold text-white mb-2">100% On-Chain Data Storage</h4>
                <p className="text-slate-300 mb-3">
                  Every aspect of the game is stored and verifiable on the Base blockchain. No off-chain databases or hidden game state - everything is publicly auditable on BaseScan.
                </p>
                <div className="space-y-2">
                  <div className="bg-slate-800/50 rounded p-3">
                    <p className="font-semibold text-blue-300 mb-1">On-Chain Data Includes:</p>
                    <ul className="list-disc list-inside space-y-1 text-slate-300">
                      <li>Battle outcomes & history</li>
                      <li>Player experience & statistics</li>
                      <li>Rarity rolls & randomness</li>
                      <li>Buff activation system</li>
                      <li>Staking positions & history</li>
                      <li>Revenue share & weekly distribution</li>
                      <li>Tournament data & results</li>
                      <li>NFT ownership & usage</li>
                      <li>Token balances & transfers</li>
                    </ul>
                  </div>
                </div>
              </div>
              <div>
                <h4 className="font-semibold text-white mb-2">Battle Types</h4>
                <div className="space-y-3">
                  <div className="bg-slate-800/50 rounded p-3">
                    <p className="font-semibold text-blue-300 mb-1">Player vs Player (P2P)</p>
                    <ul className="space-y-1 text-slate-300">
                      <li><strong>Contract:</strong> P2PBattleManagerV2 (<ContractLink address="0xAb19009863bf91b38AEBF753172edae4dB31f283" network="sepolia" />)</li>
                      <li><strong>Stake:</strong> 50 FarmPro per player (100 FarmPro total pool)</li>
                      <li><strong>Timeout:</strong> 3 minutes (quick matches)</li>
                      <li><strong>Reward:</strong> Winner receives 85 FarmPro (70% ROI)</li>
                      <li><strong>Burn:</strong> 15 FarmPro burned per battle</li>
                    </ul>
                  </div>
                  <div className="bg-slate-800/50 rounded p-3">
                    <p className="font-semibold text-blue-300 mb-1">Player vs Environment (PvE)</p>
                    <ul className="space-y-1 text-slate-300">
                      <li><strong>Contract:</strong> PvEManagerV7 (<ContractLink address="0x4E5a5098b1eB82A9cd7C87d5d4487F8C77a8CecD" network="sepolia" />)</li>
                      <li><strong>Stake:</strong> 50 FarmPro (player only)</li>
                      <li><strong>Timeout:</strong> 24 hours</li>
                      <li><strong>Reward:</strong> Winner receives 85 FarmPro</li>
                      <li><strong>Burn:</strong> 15 FarmPro on win/loss, 20 FarmPro on draw</li>
                      <li><strong>Modes:</strong> On-Chain Mode (fully decentralized) or Hybrid Mode (real-time + final transaction)</li>
                    </ul>
                  </div>
                </div>
              </div>
              <div>
                <h4 className="font-semibold text-white mb-2">Damage Calculation Formula</h4>
                <div className="bg-slate-800/50 rounded p-3 font-mono text-xs space-y-1">
                  <p>FinalDamage = BaseDamage + ElementModifier + RarityBonus + BuffBonus + PotionBonus - DefenseReduction</p>
                  <p className="mt-2">BaseDamage:</p>
                  <p>  WIN:   30</p>
                  <p>  DRAW:  15</p>
                  <p>  LOSE:  10</p>
                  <p className="mt-2">ElementModifier:</p>
                  <p>  ADVANTAGE:   +10</p>
                  <p>  NEUTRAL:     0</p>
                  <p>  DISADVANTAGE: -5</p>
                  <p className="mt-2">RarityBonus:</p>
                  <p>  DIAMOND: +50</p>
                  <p>  GOLD:    +25</p>
                  <p>  SILVER:  +10</p>
                  <p>  BRONZE:  +5</p>
                </div>
              </div>
              <div>
                <h4 className="font-semibold text-white mb-2">Element System</h4>
                <div className="bg-slate-800/50 rounded p-3">
                  <p className="font-semibold text-blue-300 mb-2">Element Cycle:</p>
                  <p className="text-slate-300 mb-2">Water &gt; Fire &gt; Ice &gt; Wind &gt; Water (cyclic advantage)</p>
                  <p className="text-slate-300">Non-adjacent elements = DRAW (no advantage)</p>
                </div>
              </div>
            </div>
          </AccordionItem>
          </div>

          {/* NFT System */}
          <div id="nft-system">
            <AccordionItem id="nft-system" title="NFT System: ERC1155 Multi-Token Standard" icon="🛡️" isOpen={isSectionOpen('nft-system')} onToggle={() => toggleSection('nft-system')}>
            <div className="space-y-4 text-xs">
              <div>
                <h4 className="font-semibold text-white mb-2">Buff NFTs (Buff1155)</h4>
                <div className="bg-slate-800/50 rounded p-3 space-y-2">
                  <p><strong>Contract Address:</strong> <ContractLink address="0xB9056C235C5BAE9c4eF76A6130bA670DF4e267EF" network="sepolia" /></p>
                  <p className="font-semibold text-blue-300 mb-1">Token IDs:</p>
                  <ul className="list-disc list-inside space-y-1 text-slate-300">
                    <li><strong>0:</strong> Shield (25% damage reduction)</li>
                    <li><strong>1:</strong> Pierce (reduces enemy defense)</li>
                    <li><strong>2:</strong> Haste (+5 flat damage)</li>
                    <li><strong>3:</strong> Frost (freeze opponent, skip turn)</li>
                  </ul>
                  <p className="font-semibold text-blue-300 mt-3 mb-1">Features:</p>
                  <ul className="list-disc list-inside space-y-1 text-slate-300">
                    <li>ERC1155 Standard: Multi-token NFT (fungible within type)</li>
                    <li>Daily Usage Limits: Each buff usable once per day per player</li>
                    <li>Marketplace Integration: Buy/sell buffs with FarmPro, ETH, or USDC</li>
                    <li>Consumable: Buffs are burned after use in battle</li>
                  </ul>
                </div>
              </div>
              <div>
                <h4 className="font-semibold text-white mb-2">Potion NFTs</h4>
                <div className="space-y-2">
                  <div className="bg-slate-800/50 rounded p-3">
                    <p className="font-semibold text-blue-300 mb-1">Craftable Potions (Potion1155)</p>
                    <p className="text-slate-300"><strong>Contract:</strong> <ContractLink address="0x99593F655bEA59C8cc094776bCEe2C377b1c4e7a" network="sepolia" /></p>
                    <p className="text-slate-300">Players craft potions using materials</p>
                  </div>
                  <div className="bg-slate-800/50 rounded p-3">
                    <p className="font-semibold text-blue-300 mb-1">Classic Potions (ClassicPotion1155)</p>
                    <p className="text-slate-300"><strong>Contract:</strong> <ContractLink address="0x5c51846f484Ad68df12253621188069205cFC04E" network="sepolia" /></p>
                    <p className="text-slate-300">Direct minting (not crafted)</p>
                  </div>
                </div>
              </div>
            </div>
          </AccordionItem>
          </div>

          {/* Staking & Revenue Sharing */}
          <div id="staking-revenue">
            <AccordionItem id="staking-revenue" title="Staking & Revenue Sharing System" icon="💰" isOpen={isSectionOpen('staking-revenue')} onToggle={() => toggleSection('staking-revenue')}>
            <div className="space-y-4 text-xs">
              <div>
                <h4 className="font-semibold text-white mb-2">Staking Manager V4</h4>
                <div className="bg-slate-800/50 rounded p-3 space-y-2">
                  <p><strong>Contract Address:</strong> <ContractLink address="0xbcB64EE0b6bbb49EB16C94C02B7507C65972B127" network="sepolia" /></p>
                  <p className="font-semibold text-blue-300 mb-1">Staking Tiers:</p>
                  <div className="space-y-2">
                    <div>
                      <p className="font-semibold text-green-300">ACCESS Tier: 500+ FarmPro staked</p>
                      <ul className="list-disc list-inside space-y-1 text-slate-300 ml-2">
                        <li>Quest Access: Can complete daily quests</li>
                        <li>Marketplace Discount: 50% off all NFT purchases</li>
                        <li>Revenue Share: Eligible for weekly revenue distribution</li>
                      </ul>
                    </div>
                    <div>
                      <p className="font-semibold text-green-300">MAX Tier: 10,000+ FarmPro staked</p>
                      <ul className="list-disc list-inside space-y-1 text-slate-300 ml-2">
                        <li>All ACCESS benefits</li>
                        <li>Priority Tournament Access: Guaranteed tournament slots</li>
                      </ul>
                    </div>
                  </div>
                  <p className="font-semibold text-blue-300 mt-3 mb-1">Unbonding Period:</p>
                  <p className="text-slate-300">3 days (259,200 seconds) - prevents flash loan attacks</p>
                </div>
              </div>
              <div>
                <h4 className="font-semibold text-white mb-2">Revenue Sharing V4</h4>
                <div className="bg-slate-800/50 rounded p-3 space-y-2">
                  <p><strong>Contract Address:</strong> <ContractLink address="0x8Ca66F5ba3Daa478a45814b5b59eD21750Dc1804" network="sepolia" /></p>
                  <p className="font-semibold text-blue-300 mb-1">Revenue Sources:</p>
                  <ul className="list-disc list-inside space-y-1 text-slate-300">
                    <li>Buff1155 Marketplace: 50% of sales revenue</li>
                    <li>ClassicPotion1155 Marketplace: 50% of sales revenue</li>
                  </ul>
                  <p className="font-semibold text-blue-300 mt-3 mb-1">Distribution Method:</p>
                  <p className="text-slate-300">Time-weighted average (stake-seconds) for fair distribution</p>
                  <p className="text-slate-300">Weekly distribution: Monday 00:00 UTC to Sunday 23:59 UTC</p>
                  <p className="text-slate-300">Multi-token support: ETH, USDC, FarmPro distributed separately</p>
                </div>
              </div>
              <div>
                <h4 className="font-semibold text-white mb-2">Staking Mirror</h4>
                <div className="bg-slate-800/50 rounded p-3">
                  <p><strong>Contract Address:</strong> <ContractLink address="0x0a89D5e0c759A87dB0a1Bd6EaaE81C97c3744A68" network="sepolia" /></p>
                  <p className="text-slate-300 mt-2">
                    Maintains 50/50 stake ratio between players and backend. When player stakes X FarmPro, backend auto-stakes X FarmPro via StakingMirror.
                  </p>
                </div>
              </div>
            </div>
          </AccordionItem>
          </div>

          {/* Tournament System */}
          <div id="tournament-system">
            <AccordionItem id="tournament-system" title="Tournament System" icon="🏆" isOpen={isSectionOpen('tournament-system')} onToggle={() => toggleSection('tournament-system')}>
            <div className="space-y-4 text-xs">
              <div>
                <h4 className="font-semibold text-white mb-2">Round-Robin Tournament Contract</h4>
                <div className="bg-slate-800/50 rounded p-3 space-y-2">
                  <p><strong>Contract Address:</strong> <ContractLink address="0xa841f64dC0c58dCd18307C5A006Ef17C56397e41" network="sepolia" /></p>
                  <p className="font-semibold text-blue-300 mb-1">Tournament Structure:</p>
                  <ul className="list-disc list-inside space-y-1 text-slate-300">
                    <li><strong>Format:</strong> Round-robin (every player plays every other player)</li>
                    <li><strong>Min Players:</strong> 3</li>
                    <li><strong>Max Players:</strong> 10 (configurable up to 50)</li>
                    <li><strong>Entry Fee:</strong> 50 FarmPro per player</li>
                    <li><strong>Prize Pool:</strong> Treasury-backed minimums + player fees</li>
                  </ul>
                </div>
              </div>
              <div>
                <h4 className="font-semibold text-white mb-2">Prize Distribution</h4>
                <div className="bg-slate-800/50 rounded p-3 font-mono text-xs space-y-1">
                  <p>First Place:  150 FarmPro (minimum, treasury-backed)</p>
                  <p>Second Place: 100 FarmPro (minimum, treasury-backed)</p>
                  <p>Third Place:   50 FarmPro (minimum, treasury-backed)</p>
                  <p className="mt-2">If 6+ players (300+ FarmPro collected):</p>
                  <p>  Prize pool fully funded by entry fees</p>
                  <p className="mt-2">If &lt;6 players:</p>
                  <p>  Treasury funds prize pool gap</p>
                </div>
              </div>
            </div>
          </AccordionItem>
          </div>

          {/* Game Treasury */}
          <div id="game-treasury">
            <AccordionItem id="game-treasury" title="Game Treasury" icon="🏛️" isOpen={isSectionOpen('game-treasury')} onToggle={() => toggleSection('game-treasury')}>
            <div className="space-y-4 text-xs">
              <div className="bg-slate-800/50 rounded p-3 space-y-2">
                <p><strong>Contract Address:</strong> <ContractLink address="0x383352A1e8bB0B409FCc60e5eCeBAB76258cDA7c" network="sepolia" /></p>
                <p className="text-slate-300">
                  Unified treasury for all game rewards including quest rewards, tournament prizes, and ranking rewards.
                </p>
                <p className="font-semibold text-blue-300 mt-3 mb-1">Reward Types:</p>
                <ul className="list-disc list-inside space-y-1 text-slate-300">
                  <li><strong>Quest Rewards (Type 1):</strong> Daily quest completion bonuses</li>
                  <li><strong>Tournament Prizes (Type 2):</strong> Top 3 finishers</li>
                  <li><strong>Weekly Ranking (Type 3):</strong> Weekly leaderboard rewards</li>
                  <li><strong>Monthly Ranking (Type 4):</strong> Monthly leaderboard rewards</li>
                </ul>
              </div>
            </div>
          </AccordionItem>
          </div>

          {/* Security Architecture */}
          <div id="security-architecture">
            <AccordionItem id="security-architecture" title="Security Architecture" icon="🔒" isOpen={isSectionOpen('security-architecture')} onToggle={() => toggleSection('security-architecture')}>
            <div className="space-y-4 text-xs">
              <div>
                <h4 className="font-semibold text-white mb-2">Access Control</h4>
                <div className="bg-slate-800/50 rounded p-3 space-y-2">
                  <p className="font-semibold text-blue-300 mb-1">Multi-Layer Authorization:</p>
                  <ul className="list-disc list-inside space-y-1 text-slate-300">
                    <li><strong>Owner-Only Functions:</strong> Contract deployment, emergency pause, trusted contract whitelisting</li>
                    <li><strong>Authorized Backend Functions:</strong> Battle creation, battle resolution, reward distribution, tournament management</li>
                    <li><strong>Public Functions:</strong> Battle payments, staking/unstaking, reward claiming, NFT marketplace</li>
                  </ul>
                </div>
              </div>
              <div>
                <h4 className="font-semibold text-white mb-2">Reentrancy Protection</h4>
                <div className="bg-slate-800/50 rounded p-3">
                  <p className="text-slate-300">
                    All contracts use OpenZeppelin's ReentrancyGuard and follow the Check-Effect-Interaction pattern to prevent double-spending attacks.
                  </p>
                </div>
              </div>
              <div>
                <h4 className="font-semibold text-white mb-2">Emergency Withdrawal System</h4>
                <div className="bg-slate-800/50 rounded p-3 space-y-2">
                  <p className="font-semibold text-blue-300 mb-1">StakingManagerV4:</p>
                  <ul className="list-disc list-inside space-y-1 text-slate-300">
                    <li>2-of-2 Multi-Sig: Both signers must approve</li>
                    <li>51% Staker Vote: Stake-weighted voting required</li>
                    <li>Recipient Restriction: Can only withdraw to signer addresses</li>
                  </ul>
                  <p className="font-semibold text-blue-300 mt-3 mb-1">StakingRevenueShareV4:</p>
                  <ul className="list-disc list-inside space-y-1 text-slate-300">
                    <li>1 Signer Approval: One signer approves (simplified flow)</li>
                    <li>51% Staker Vote: Same voting requirement</li>
                  </ul>
                </div>
              </div>
            </div>
          </AccordionItem>
          </div>

          {/* Economic Models */}
          <div id="economic-models">
            <AccordionItem id="economic-models" title="Economic Models & Formulas" icon="📊" isOpen={isSectionOpen('economic-models')} onToggle={() => toggleSection('economic-models')}>
            <div className="space-y-4 text-xs">
              <div>
                <h4 className="font-semibold text-white mb-2">Battle Economics</h4>
                <div className="bg-slate-800/50 rounded p-3 space-y-2">
                  <p className="font-semibold text-blue-300 mb-1">P2P Battle ROI:</p>
                  <p className="text-slate-300">Investment: 50 FarmPro</p>
                  <p className="text-slate-300">Win Probability: 50% (assuming equal skill)</p>
                  <p className="text-slate-300">Win Reward: 85 FarmPro</p>
                  <p className="text-slate-300">Expected Value: 17.5 FarmPro per battle (35% ROI)</p>
                </div>
              </div>
              <div>
                <h4 className="font-semibold text-white mb-2">Staking Economics</h4>
                <div className="bg-slate-800/50 rounded p-3">
                  <p className="text-slate-300">
                    Stakers earn weekly revenue share from marketplace (ETH, USDC, FarmPro) based on time-weighted stake-seconds. APY varies based on marketplace activity and staker count.
                  </p>
                </div>
              </div>
              <div>
                <h4 className="font-semibold text-white mb-2">Deflationary Pressure</h4>
                <div className="bg-slate-800/50 rounded p-3">
                  <p className="text-slate-300 mb-2">
                    <strong>Daily Burn Estimate (1000 battles/day):</strong>
                  </p>
                  <ul className="list-disc list-inside space-y-1 text-slate-300">
                    <li>Battle Burns: 15,000 FarmPro/day</li>
                    <li>NFT Burns: ~3,000 NFTs/day (value equivalent)</li>
                    <li>Marketplace Fee Burns: Variable (2-5% of transactions)</li>
                    <li>Total Daily Burn: ~16,000-20,000 FarmPro/day</li>
                    <li>Annual Burn: ~5.8M-7.3M FarmPro/year</li>
                  </ul>
                </div>
              </div>
            </div>
          </AccordionItem>
          </div>

          {/* Contract Addresses */}
          <div id="contract-addresses">
            <AccordionItem id="contract-addresses" title="Contract Addresses (Base Sepolia)" icon="🔗" isOpen={isSectionOpen('contract-addresses')} onToggle={() => toggleSection('contract-addresses')}>
            <div className="space-y-4 text-xs">
              <div>
                <h4 className="font-semibold text-white mb-2">Core Contracts</h4>
                <div className="bg-slate-800/50 rounded p-3 space-y-1 font-mono text-xs">
                  <p><strong>FarmPro Token:</strong> <ContractLink address="0x0a862e9bE2716162f14BE1cb500b29354ED3D434" network="sepolia" /></p>
                  <p><strong>AirdropManager V2:</strong> <ContractLink address="0x069CeAE653b429Bd0a397BaF4d5aCccC6dAd991a" network="sepolia" /></p>
                  <p><strong>AirdropManager V3:</strong> <ContractLink address="0x77CA54DF20eC45ae6cD4034623BD24129138E818" network="sepolia" /></p>
                  <p><strong>RNG Beacon:</strong> <ContractLink address="0xFCBbe67fA163b8526Cb6882b2b4144aF33dE3082" network="sepolia" /></p>
                  <p><strong>Player XP:</strong> <ContractLink address="0x4D8987BAAcbFeCDa9975b051A4a67737926879D5" network="sepolia" /></p>
                  <p><strong>Game Treasury:</strong> <ContractLink address="0x383352A1e8bB0B409FCc60e5eCeBAB76258cDA7c" network="sepolia" /></p>
                </div>
              </div>
              <div>
                <h4 className="font-semibold text-white mb-2">Battle Contracts</h4>
                <div className="bg-slate-800/50 rounded p-3 space-y-1 font-mono text-xs">
                  <p><strong>P2P Manager V2:</strong> <ContractLink address="0xAb19009863bf91b38AEBF753172edae4dB31f283" network="sepolia" /></p>
                  <p><strong>PvE Manager V7:</strong> <ContractLink address="0x4E5a5098b1eB82A9cd7C87d5d4487F8C77a8CecD" network="sepolia" /></p>
                  <p><strong>Battle Bot:</strong> <ContractLink address="0x88e1e984881CBd756a5d6B398B64d6493047b208" network="sepolia" /></p>
                </div>
              </div>
              <div>
                <h4 className="font-semibold text-white mb-2">NFT Contracts</h4>
                <div className="bg-slate-800/50 rounded p-3 space-y-1 font-mono text-xs">
                  <p><strong>Buff1155:</strong> <ContractLink address="0xB9056C235C5BAE9c4eF76A6130bA670DF4e267EF" network="sepolia" /></p>
                  <p><strong>Potion1155:</strong> <ContractLink address="0x99593F655bEA59C8cc094776bCEe2C377b1c4e7a" network="sepolia" /></p>
                  <p><strong>ClassicPotion1155:</strong> <ContractLink address="0x5c51846f484Ad68df12253621188069205cFC04E" network="sepolia" /></p>
                </div>
              </div>
              <div>
                <h4 className="font-semibold text-white mb-2">Staking & Revenue</h4>
                <div className="bg-slate-800/50 rounded p-3 space-y-1 font-mono text-xs">
                  <p><strong>StakingManager V4:</strong> <ContractLink address="0xbcB64EE0b6bbb49EB16C94C02B7507C65972B127" network="sepolia" /></p>
                  <p><strong>StakingRevenueShare V4:</strong> <ContractLink address="0x8Ca66F5ba3Daa478a45814b5b59eD21750Dc1804" network="sepolia" /></p>
                  <p><strong>StakingMirror:</strong> <ContractLink address="0x0a89D5e0c759A87dB0a1Bd6EaaE81C97c3744A68" network="sepolia" /></p>
                </div>
              </div>
              <div>
                <h4 className="font-semibold text-white mb-2">Tournament</h4>
                <div className="bg-slate-800/50 rounded p-3 space-y-1 font-mono text-xs">
                  <p><strong>RoundRobinTournament:</strong> <ContractLink address="0xa841f64dC0c58dCd18307C5A006Ef17C56397e41" network="sepolia" /></p>
                </div>
              </div>
            </div>
          </AccordionItem>
          </div>

          {/* Recent Updates */}
          <div id="recent-updates">
            <AccordionItem id="recent-updates" title="Recent Updates & Enhancements (Since November 29, 2025)" icon="📅" isOpen={isSectionOpen('recent-updates')} onToggle={() => toggleSection('recent-updates')}>
            <div className="space-y-4 text-xs">
              <div className="bg-slate-800/50 rounded p-3 space-y-3">
                <div>
                  <p className="font-semibold text-blue-300 mb-1">Version 6.10.1 - Official Song Popup & Audio Improvements (December 1, 2025)</p>
                  <ul className="list-disc list-inside space-y-1 text-slate-300 ml-2">
                    <li>Official Song Popup in TopNav</li>
                    <li>Global Audio Instance</li>
                    <li>Sound Settings in Player Settings</li>
                    <li>Base App Audio Fix</li>
                  </ul>
                </div>
                <div>
                  <p className="font-semibold text-blue-300 mb-1">Version 6.9.9 - StakingMirror Critical Fix (December 1, 2025)</p>
                  <p className="text-slate-300">Fixed duplicate event processing causing wrong amounts</p>
                </div>
                <div>
                  <p className="font-semibold text-blue-300 mb-1">Version 6.9.0 - P2P Reconnection System (December 1, 2025)</p>
                  <p className="text-slate-300">Players can navigate away and return to their pending match</p>
                </div>
                <div>
                  <p className="font-semibold text-blue-300 mb-1">Version 6.8.0 - Modal System Refactoring (December 1, 2025)</p>
                  <p className="text-slate-300">Created centralized modal management with useModal hook</p>
                </div>
                <div>
                  <p className="font-semibold text-blue-300 mb-1">Version 6.7.0 - Quest System (November 29, 2025)</p>
                  <p className="text-slate-300">Complete quest system with 30 quests total (10 daily, 10 weekly, 10 monthly)</p>
                </div>
                <p className="text-slate-400 text-xs mt-4">
                  See full changelog for complete list of updates and enhancements.
                </p>
              </div>
            </div>
          </AccordionItem>
          </div>

          {/* Roadmap */}
          <div id="roadmap">
            <AccordionItem id="roadmap" title="Roadmap & Future Development" icon="🚀" isOpen={isSectionOpen('roadmap')} onToggle={() => toggleSection('roadmap')}>
            <div className="space-y-4 text-xs">
              <div>
                <h4 className="font-semibold text-white mb-2">Phase 1: Core Game (✅ Completed)</h4>
                <ul className="list-disc list-inside space-y-1 text-slate-300">
                  <li>✅ Battle system (P2P & PvE)</li>
                  <li>✅ Chainlink VRF integration</li>
                  <li>✅ NFT system (Buffs & Potions)</li>
                  <li>✅ Staking & revenue sharing</li>
                  <li>✅ Tournament system</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-white mb-2">Phase 2: Enhanced Features (✅ Completed)</h4>
                <ul className="list-disc list-inside space-y-1 text-slate-300">
                  <li>✅ Buff activation on-chain</li>
                  <li>✅ Quest system with on-chain verification</li>
                  <li>✅ AirdropManagerV3 with variable amounts</li>
                  <li>✅ StakingManagerV4 with 2-of-2 multi-sig</li>
                  <li>🔄 Advanced tournament formats (elimination, Swiss)</li>
                  <li>🔄 Player rankings & leaderboards</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-white mb-2">Phase 3: Ecosystem Expansion (📅 Planned)</h4>
                <ul className="list-disc list-inside space-y-1 text-slate-300">
                  <li>📅 Cross-chain bridge (Ethereum, Polygon, Arbitrum)</li>
                  <li>📅 Mobile app (iOS & Android)</li>
                  <li>📅 Governance token (DAO voting)</li>
                  <li>📅 Creator economy (user-generated content)</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-white mb-2">Phase 4: Advanced Features (🔮 Future)</h4>
                <ul className="list-disc list-inside space-y-1 text-slate-300">
                  <li>🔮 Advanced difficulty scaling</li>
                  <li>🔮 Guild system (team battles)</li>
                  <li>🔮 Land ownership (metaverse integration)</li>
                  <li>🔮 Interoperability (NFTs usable in other games)</li>
                </ul>
              </div>
            </div>
          </AccordionItem>
          </div>

          {/* Investment Thesis */}
          <div id="investment-thesis">
            <AccordionItem id="investment-thesis" title="Investment Thesis" icon="📈" isOpen={isSectionOpen('investment-thesis')} onToggle={() => toggleSection('investment-thesis')}>
            <div className="space-y-4 text-xs">
              <div>
                <h4 className="font-semibold text-white mb-2">Market Opportunity</h4>
                <div className="bg-slate-800/50 rounded p-3 space-y-2">
                  <p className="font-semibold text-blue-300 mb-1">Blockchain Gaming Market:</p>
                  <ul className="list-disc list-inside space-y-1 text-slate-300">
                    <li><strong>Market Size:</strong> $4.6B in 2022, projected $65.7B by 2027 (CAGR: 70.3%)</li>
                    <li><strong>User Base:</strong> 1.2M+ blockchain gamers globally</li>
                    <li><strong>Growth Driver:</strong> Play-to-earn mechanics + NFT ownership</li>
                  </ul>
                </div>
              </div>
              <div>
                <h4 className="font-semibold text-white mb-2">Competitive Advantages</h4>
                <ul className="list-disc list-inside space-y-2 text-slate-300">
                  <li><strong>1. Fully On-Chain Game Logic:</strong> Battle outcomes verifiable on-chain, no server-side manipulation possible</li>
                  <li><strong>2. Chainlink VRF Integration:</strong> Industry-leading oracle for randomness, provably fair game mechanics</li>
                  <li><strong>3. Sustainable Token Economics:</strong> Deflationary burn mechanisms, revenue sharing for stakers</li>
                  <li><strong>4. Security-First Architecture:</strong> Multi-sig governance, emergency withdrawal mechanisms</li>
                  <li><strong>5. Zora Creator Coin Integration:</strong> FarmPro token leverages Zora's creator economy</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-white mb-2">Revenue Streams</h4>
                <ul className="list-disc list-inside space-y-1 text-slate-300">
                  <li><strong>Battle Fees:</strong> 15% burn rate creates deflationary pressure</li>
                  <li><strong>NFT Marketplace:</strong> 50% revenue share to stakers</li>
                  <li><strong>Tournament Entry Fees:</strong> Treasury-backed prize pools</li>
                  <li><strong>Staking Revenue Share:</strong> Weekly distribution from marketplace</li>
                </ul>
              </div>
            </div>
          </AccordionItem>
          </div>

          {/* Contact & Resources */}
          <div id="contact-resources">
            <AccordionItem id="contact-resources" title="Contact & Resources" icon="📞" isOpen={isSectionOpen('contact-resources')} onToggle={() => toggleSection('contact-resources')}>
            <div className="space-y-4 text-xs">
              <div>
                <h4 className="font-semibold text-white mb-2">Official Links</h4>
                <div className="bg-slate-800/50 rounded p-3 space-y-1">
                  <p className="text-slate-300"><strong>Website:</strong> https://origamibattle.app</p>
                  <p className="text-slate-300"><strong>BaseScan:</strong> https://sepolia.basescan.org</p>
                  <p className="text-slate-300"><strong>Documentation:</strong> https://docs.origamibattle.app</p>
                </div>
              </div>
            </div>
          </AccordionItem>
          </div>

          {/* Conclusion */}
          <div id="conclusion">
            <AccordionItem id="conclusion" title="Conclusion" icon="📝" isOpen={isSectionOpen('conclusion')} onToggle={() => toggleSection('conclusion')}>
            <div className="space-y-4 text-xs">
              <p className="text-slate-300">
                Origami Battles represents a paradigm shift in blockchain gaming, combining strategic gameplay with fully on-chain mechanics, verifiable randomness, and sustainable token economics. Built on Base (Coinbase's Ethereum L2) and leveraging Chainlink VRF for provably fair randomness, the game offers:
              </p>
              <ul className="list-disc list-inside space-y-2 text-slate-300">
                <li><strong>100% On-Chain Game Logic:</strong> Transparent, verifiable, and tamper-proof</li>
                <li><strong>Chainlink VRF Integration:</strong> Industry-leading oracle for randomness</li>
                <li><strong>Sustainable Token Economics:</strong> Deflationary mechanisms + revenue sharing</li>
                <li><strong>Security-First Architecture:</strong> Multi-sig governance, emergency withdrawals, comprehensive access controls</li>
                <li><strong>Zora Creator Coin Integration:</strong> FarmPro token leverages creator economy features</li>
              </ul>
              <div className="bg-blue-900/20 border border-blue-700/50 rounded p-3 mt-4">
                <p className="font-semibold text-blue-300 mb-2">Investment Opportunity</p>
                <p className="text-slate-300 text-xs">
                  The game's deflationary tokenomics, revenue-sharing model, and growing player base create multiple value accrual vectors for token holders. Early investors stand to benefit from token appreciation, staking rewards, tournament prizes, and NFT value appreciation.
                </p>
              </div>
              <p className="text-slate-400 text-xs mt-4 italic">
                This document is for informational purposes only and does not constitute investment advice. Always conduct your own research and consult with financial advisors before making investment decisions.
              </p>
            </div>
          </AccordionItem>
          </div>
          
        </div>
      </div>
    </main>
  )
}


